/**
 * useModalNavigation Hook
 *
 * Manages modal navigation functionality including:
 * - Arrow key navigation between assets
 * - Asset sorting and indexing
 * - Modal asset switching logic
 */

import { useCallback } from 'react'

export const useModalNavigation = () => {
  const getAssetDate = useCallback(asset => {
    return new Date(asset?.metadata?.content?.date ?? asset?.content?.date ?? 0)
  }, [])

  const getSortedAssets = useCallback(
    assets => {
      if (!assets || typeof assets !== 'object') {
        return []
      }
      return Object.values(assets).sort(
        (a, b) => getAssetDate(a) - getAssetDate(b)
      )
    },
    [getAssetDate]
  )

  const handleModalArrowClick = useCallback(
    (props, direction) => {
      if (!props?.state) {
        console.error('Modal navigation: props.state is undefined')
        return
      }
      let assetsToUse = props.state.publishedAssets || props.state.assets
      let sortedAssets
      if (Array.isArray(assetsToUse)) {
        sortedAssets = assetsToUse.sort(
          (a, b) => getAssetDate(a) - getAssetDate(b)
        )
      } else {
        sortedAssets = getSortedAssets(assetsToUse)
      }

      if (!sortedAssets.length) {
        console.error('No assets available for navigation')
        return
      }

      const currentAssetId =
        props.state?.modalData?.assetId ?? props.state?.currentAsset

      if (!currentAssetId) {
        console.error('No current asset ID found')
        return
      }

      const currentIndex = sortedAssets.findIndex(a => a.id === currentAssetId)

      if (currentIndex === -1) {
        console.error('Current asset not found in sorted assets')
        return
      }

      let newIndex = currentIndex
      if (direction === 'left') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : sortedAssets.length - 1
      } else if (direction === 'right') {
        newIndex = currentIndex < sortedAssets.length - 1 ? currentIndex + 1 : 0
      }

      const newAsset = sortedAssets[newIndex]
      // Update modal data to show new asset
      props.dispatch({
        type: 'set_modal_data',
        payload: { asset: newAsset, assetId: newAsset.id }
      })
    },
    [getSortedAssets, getAssetDate]
  )

  const handleModalStatusChange = useCallback((props, status) => {
    props.dispatch({ type: 'set_modal_status', payload: status })
  }, [])

  const handleModalOpen = useCallback(props => {
    if (!props?.state?.publishedAssets) {
      console.error('Modal open: publishedAssets is undefined')
      return
    }

    let assetToBeDisplayed
    if ('assetId' in props.payload) {
      const assetId = props.payload.assetId
      assetToBeDisplayed = props.state.publishedAssets.find(
        asset => asset.id === assetId
      )
    } else if ('assetIdx' in props.payload) {
      const assetIdx = props.payload.assetIdx
      assetToBeDisplayed = props.state.publishedAssets[assetIdx]
    }

    if (assetToBeDisplayed) {
      props.dispatch({
        type: 'set_modal_data',
        payload: {
          asset: assetToBeDisplayed,
          assetId: assetToBeDisplayed.id
        }
      })
      props.dispatch({ type: 'set_modal_active', payload: true })
    } else {
      console.error('Asset not found.')
    }
  }, [])

  const handleModalClose = useCallback((props, e) => {
    if (
      e.target.classList.contains('modal') ||
      e.target.classList.contains('close-modal-icon')
    ) {
      props?.dispatch({ type: 'set_modal_active', payload: false })
      props?.dispatch({ type: 'set_modal_status', payload: 'Image' })

      if (typeof props.onClose === 'function') {
        props.onClose()
      } else {
        console.error('onClose is not available or not a function', props)
      }
    }
  }, [])

  return {
    handleModalArrowClick,
    handleModalStatusChange,
    handleModalOpen,
    handleModalClose,
    getSortedAssets,
    getAssetDate
  }
}

export default useModalNavigation
